import { useEffect, useState } from 'react';
import { Button } from '@/ui/Button';
import { Drawer } from '@/ui/Drawer';
import { Field, Input, Select, Textarea } from '@/ui/Field';
import { MODALIDADES } from './piezas';
import {
  TOPE_MB_POR_FOTO, aHoraLocal, desdeHoraLocal, useGuardarEvento,
  type EventoConGaleria, type Modalidad,
} from './api';

/**
 * FormularioDeEvento.tsx — la opción 3: crear y editar un evento.
 *
 * ## La hora que se edita es la hora local
 *
 * Ver `aHoraLocal` en `api.ts`: el vanilla rellenaba el campo con la hora UTC,
 * y guardar sin tocar nada retrasaba el evento cinco horas en Bogotá.
 *
 * ## La portada ya no duplica el evento
 *
 * Si la portada falla, el evento YA existe: el formulario se cierra y lo dice.
 * El vanilla lo dejaba abierto con los mismos datos, y volver a pulsar «Crear
 * evento» creaba otro. Ver `useGuardarEvento`.
 *
 * ## Los mismos límites que el servidor
 *
 * `EventCreate` rechaza un título vacío o de más de 200 caracteres y un cupo
 * menor que 1; la columna `location` es de 200. Se comprueban aquí para que el
 * error salga en su campo.
 *
 * ## Sin estado en `window`
 *
 * El vanilla guardaba la portada en `window._evCoverFile`, que `Convenciones.md`
 * prohíbe por nombre. Aquí vive y muere con el formulario.
 */

const MAXIMO_TITULO = 200;
const MAXIMO_LUGAR = 200;

export function FormularioDeEvento({
  abierto, evento, onCerrar,
}: {
  abierto: boolean;
  /** `null` para crear uno nuevo. */
  evento: EventoConGaleria | null;
  onCerrar: () => void;
}) {
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [fecha, setFecha] = useState('');
  const [modalidad, setModalidad] = useState<Modalidad>('presencial');
  const [lugar, setLugar] = useState('');
  const [cupo, setCupo] = useState('');
  const [fijado, setFijado] = useState(false);
  const [portada, setPortada] = useState<File | null>(null);
  const [errores, setErrores] = useState<Record<string, string>>({});
  const guardar = useGuardarEvento();

  // Se rellena al abrir, no en cada render: si no, lo que se escribe se perdería
  // en el siguiente repintado.
  useEffect(() => {
    if (!abierto) return;
    setTitulo(evento?.title ?? '');
    setDescripcion(evento?.description ?? '');
    setFecha(evento ? aHoraLocal(evento.event_date) : '');
    setModalidad((evento?.modality as Modalidad) ?? 'presencial');
    setLugar(evento?.location ?? '');
    setCupo(evento?.max_capacity != null ? String(evento.max_capacity) : '');
    setFijado(evento?.is_pinned_on_join ?? false);
    setPortada(null);
    setErrores({});
  }, [abierto, evento]);

  function validar(): boolean {
    const e: Record<string, string> = {};
    const t = titulo.trim();
    if (!t) e.titulo = 'El evento necesita un título.';
    else if (t.length > MAXIMO_TITULO) e.titulo = `El título no puede pasar de ${MAXIMO_TITULO} caracteres.`;
    if (!fecha) e.fecha = 'Indica el día y la hora.';
    if (lugar.trim().length > MAXIMO_LUGAR) e.lugar = `El lugar no puede pasar de ${MAXIMO_LUGAR} caracteres.`;
    if (cupo && (!/^\d+$/.test(cupo) || Number(cupo) < 1)) {
      e.cupo = 'El cupo debe ser un número entero de al menos 1. Déjalo vacío si no hay límite.';
    }
    if (portada && portada.size > TOPE_MB_POR_FOTO * 1024 * 1024) {
      e.portada = `La imagen pesa ${(portada.size / 1024 / 1024).toFixed(1)} MB; el máximo es ${TOPE_MB_POR_FOTO} MB.`;
    }
    setErrores(e);
    return Object.keys(e).length === 0;
  }

  function enviar() {
    if (!validar()) return;
    guardar.mutate(
      {
        id: evento?.id ?? null,
        campos: {
          title: titulo.trim(),
          description: descripcion.trim() || null,
          event_date: desdeHoraLocal(fecha),
          location: lugar.trim() || null,
          modality: modalidad,
          max_capacity: cupo ? Number(cupo) : null,
          is_pinned_on_join: fijado,
        },
        portada,
      },
      {
        onSuccess: ({ errorDePortada }) => {
          if (errorDePortada) {
            const motivo = errorDePortada instanceof Error ? errorDePortada.message : 'error desconocido';
            Utils.toast(
              `${evento ? 'Los cambios se guardaron' : 'El evento se creó'}, pero la portada no se subió (${motivo}). ` +
                'Puedes volver a intentarlo desde «Editar».',
              'warning',
              8000,
            );
          } else {
            Utils.toast(evento ? 'Evento actualizado' : 'Evento creado', 'success');
          }
          onCerrar();
        },
        onError: (err) =>
          Utils.toast(err instanceof Error ? err.message : 'No se pudo guardar el evento', 'error'),
      },
    );
  }

  const limpiar = (campo: string) => setErrores((x) => ({ ...x, [campo]: '' }));

  return (
    <Drawer
      abierto={abierto}
      onCambio={(v) => { if (!v) onCerrar(); }}
      titulo={evento ? 'Editar evento' : 'Nuevo evento'}
      descripcion={evento ? evento.title : 'Lo verá todo el portal en Eventos y en el calendario.'}
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
          <Button onClick={enviar} loading={guardar.isPending}>
            <i className="bi bi-floppy" aria-hidden="true" /> {evento ? 'Guardar cambios' : 'Crear evento'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Título" error={errores.titulo}>
          <Input
            value={titulo}
            maxLength={MAXIMO_TITULO}
            onChange={(e) => { setTitulo(e.target.value); limpiar('titulo'); }}
            placeholder="Nombre del evento"
          />
        </Field>

        <Field label="Descripción" opcional>
          <Textarea rows={4} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Día y hora" error={errores.fecha} ayuda="En la hora de tu equipo.">
            <Input type="datetime-local" value={fecha} onChange={(e) => { setFecha(e.target.value); limpiar('fecha'); }} />
          </Field>
          <Field label="Modalidad">
            <Select value={modalidad} onChange={(e) => setModalidad(e.target.value as Modalidad)}>
              {(Object.keys(MODALIDADES) as Modalidad[]).map((m) => (
                <option key={m} value={m}>{MODALIDADES[m].etiqueta}</option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Lugar" opcional error={errores.lugar}>
            <Input
              value={lugar}
              maxLength={MAXIMO_LUGAR}
              onChange={(e) => { setLugar(e.target.value); limpiar('lugar'); }}
              placeholder="Sala, sede o plataforma"
            />
          </Field>
          <Field label="Cupo máximo" opcional error={errores.cupo} ayuda="Vacío si no hay límite.">
            <Input
              type="number"
              inputMode="numeric"
              min={1}
              value={cupo}
              onChange={(e) => { setCupo(e.target.value); limpiar('cupo'); }}
            />
          </Field>
        </div>

        <label className="flex min-h-11 cursor-pointer items-center gap-2.5 text-3 text-ink">
          <input
            type="checkbox"
            checked={fijado}
            onChange={(e) => setFijado(e.target.checked)}
            className="size-4 accent-brand"
          />
          Fijarlo en el Inicio de todo el portal
        </label>

        <Field
          label={evento?.cover_url ? 'Cambiar la portada' : 'Portada'}
          opcional
          id="evento-portada"
          error={errores.portada}
          ayuda={`JPG o PNG, hasta ${TOPE_MB_POR_FOTO} MB. Se comprime al subirla.`}
        >
          {/* `id` y `aria-describedby` a mano: un `<input>` crudo no los toma
              del contexto de `Field`. */}
          <input
            id="evento-portada"
            aria-describedby="evento-portada-ayuda"
            aria-invalid={Boolean(errores.portada)}
            type="file"
            accept="image/*"
            onChange={(e) => { setPortada(e.target.files?.[0] ?? null); limpiar('portada'); }}
            className="block w-full text-2 file:mr-3 file:min-h-9 pointer-coarse:file:min-h-11
                       file:rounded-sm file:border-0 file:bg-sunken file:px-3 file:text-2
                       file:font-semibold file:text-ink"
          />
        </Field>
      </div>
    </Drawer>
  );
}
